import {
  encodeFrame, decodeFrame, isClientFrame,
  type ClientFrame, type ServerFrame,
} from './protocol/index.js'

// Minimal socket surface so tests can inject a fake without a real WebSocket.
export interface SocketLike {
  binaryType: string
  send(data: Uint8Array): void
  close(): void
  onopen:    (() => void) | null
  onclose:   (() => void) | null
  onerror:   (() => void) | null
  onmessage: ((ev: { data: ArrayBuffer }) => void) | null
}
export type SocketFactory = (url: string) => SocketLike

export interface ConnectionOptions {
  url:        string
  token:      string
  /** Authenticated embeds: called when the server rejects the token
   *  (typically an expired signed JWT). Return a freshly minted token to
   *  resume seamlessly, or null to give up (shows the fatal error). */
  refreshToken?: () => Promise<string | null>
  open:       ClientFrame  // frame sent right after auth (e.g. open a conversation, or subscribe_inbox)
  onFrame:    (frame: ServerFrame) => void
  getCursor:  () => number          // highest seq seen (for sync on reconnect)
  onStatusChange?: (status: 'connecting' | 'open' | 'reconnecting' | 'error', message?: string) => void
  socketFactory?: SocketFactory
  backoffBaseMs?: number
  backoffMaxMs?:  number
  maxOutbox?:     number
}

type State = 'idle' | 'connecting' | 'open' | 'closed'

export class ConnectionManager {
  private socket: SocketLike | null = null
  private state: State = 'idle'
  private authed = false
  private everAuthed = false
  private attempt = 0
  private outbox: ClientFrame[] = []
  private stopped = false
  private timer: ReturnType<typeof setTimeout> | null = null

  constructor(private readonly opts: ConnectionOptions) {}

  connect(): void {
    if (this.state === 'connecting' || this.state === 'open') return
    this.stopped = false
    this.state = 'connecting'
    this.authed = false
    this.opts.onStatusChange?.(this.attempt > 0 ? 'reconnecting' : 'connecting')
    const make = this.opts.socketFactory ?? defaultFactory
    const sock = make(this.opts.url)
    sock.binaryType = 'arraybuffer'
    this.socket = sock

    sock.onopen = () => {
      // Don't reset attempt here — reset only after successful auth ('authed').
      // A connection that opens but fails during auth (bad token, server restart)
      // should still back off, not immediately retry at base delay.
      this.raw({ type: 'auth', token: this.opts.token })
    }
    sock.onmessage = (ev) => {
      const frame = decodeFrame(new Uint8Array(ev.data))
      if (!frame || isClientFrame(frame)) return   // ignore non-server frames
      this.handle(frame)
    }
    sock.onclose = () => this.onClosed()
    sock.onerror = () => { try { sock.close() } catch { /* */ } }
  }

  /** Queue a frame; sent immediately if open, else flushed on (re)connect.
   *  The outbox is bounded so a prolonged outage can't grow memory without limit
   *  — oldest queued frames are dropped past the cap. */
  send(frame: ClientFrame): void {
    if (this.state === 'open' && this.authed) { this.raw(frame); return }
    // Evicts the oldest half when full to amortise the O(n) cost of overflow.
    this.queue(frame)
  }

  /** How many frames are waiting to go out. Useful for a host that wants to
   *  show "message pending" state, and for asserting the outbox stays bounded. */
  pendingCount(): number { return this.outbox.length }

  close(): void {
    this.stopped = true
    if (this.timer) clearTimeout(this.timer)
    this.state = 'closed'
    try { this.socket?.close() } catch { /* */ }
  }

  private handle(frame: ServerFrame): void {
    if (frame.type === 'authed') {
      this.attempt = 0   // reset backoff only after a fully successful auth
      this.state = 'open'
      this.authed = true; this.everAuthed = true
      this.opts.onStatusChange?.('open')
      // Open/resolve the conversation. The catch-up `sync` is sent on 'opened'
      // (below), i.e. only after the server has joined us to the room — sending
      // it here would race the async open and be rejected as "not joined".
      this.raw(this.opts.open)
      // The QUEUED frames need exactly the same treatment, and used to not get
      // it: flushing here fired them straight after `open`, before the server
      // had joined us, so the engine answered FORBIDDEN 'Open the conversation
      // first' and the message was gone — no ack, no requeue. This is the
      // reconnect-drops-your-message bug. Frames now wait for 'opened' below.
      //
      // EXCEPT when the open frame doesn't produce a join at all: an inbox
      // subscription (`subscribe_inbox`) never gets an 'opened' reply, so
      // waiting for one would strand the queue forever. Nothing needs joining
      // in that case, so flushing immediately is both safe and required.
      if (this.opts.open.type !== 'open') this.flush()
    }
    // 'opened' confirms we're joined — now catch up from our cursor, then
    // release anything queued while we were disconnected.
    if (frame.type === 'opened') {
      this.raw({ type: 'sync', conversationId: frame.conversation.id, sinceSeq: this.opts.getCursor() })
      this.flush(frame.conversation.id)
    }
    // A CONNECTION-FATAL error (bad or rejected token, closed or missing
    // chatroom) will never succeed on retry — stop the reconnect loop and report
    // a clear reason instead of spinning on "connecting…" forever. Per-frame
    // errors (rate limit, one bad message) are NOT fatal and fall through.
    if (frame.type === 'error' && FATAL_ERRORS.has(frame.code)) {
      // Token refresh (authenticated embeds): a signed JWT expiring mid-session
      // used to be a dead end — the widget showed a fatal error until reload.
      // If the host supplied refreshToken, ask it to mint a fresh one and
      // reconnect. One in-flight attempt at a time; a refresh that returns
      // null/throws (user logged out, backend down) falls through to fatal.
      if (frame.code === 'UNAUTHORIZED' && this.opts.refreshToken && !this.refreshing) {
        this.refreshing = true
        this.opts.onStatusChange?.('reconnecting', 'Renewing session…')
        void this.opts.refreshToken()
          .then((fresh) => {
            this.refreshing = false
            if (!fresh) { this.fatal(frame); return }
            this.opts.token = fresh
            try { this.socket?.close() } catch { /* */ }
            // onClosed schedules the reconnect, which re-auths with the new token.
          })
          .catch(() => { this.refreshing = false; this.fatal(frame) })
        return
      }
      this.fatal(frame)
      return
    }
    this.opts.onFrame(frame)
  }

  private refreshing = false

  private fatal(frame: Extract<ServerFrame, { type: 'error' }>): void {
    this.stopped = true
    try { this.socket?.close() } catch { /* */ }
    this.state = 'closed'
    this.opts.onStatusChange?.('error', friendlyError(frame.code))
    this.opts.onFrame(frame)
  }

  /** Release queued frames. When called from 'opened' we know the canonical
   *  conversation id the server just resolved us to, and queued `send` frames
   *  are retargeted to it. A manager only ever opens ONE conversation (its
   *  `opts.open`), so every queued send belongs to that thread by
   *  construction — but the id it was queued with can be STALE (queued against
   *  the previous session's conversation before a reconnect). Retargeting is a
   *  no-op in the normal case and rescues the message in the stale one. */
  private flush(conversationId?: string): void {
    const pending = this.outbox
    this.outbox = []
    for (const f of pending) {
      this.raw(
        conversationId && f.type === 'send' && f.conversationId !== conversationId
          ? { ...f, conversationId: conversationId as typeof f.conversationId }
          : f,
      )
    }
  }

  /** Bounded enqueue — the cap lives here so EVERY path that queues respects
   *  it (a failed `raw` used to push straight onto the array, bypassing it). */
  private queue(frame: ClientFrame): void {
    const cap = this.opts.maxOutbox ?? 1_000
    if (this.outbox.length >= cap) {
      this.outbox = this.outbox.slice(this.outbox.length - (cap >> 1))
    }
    this.outbox.push(frame)
  }

  private raw(frame: ClientFrame): void {
    // `this.socket?.send(...)` silently DROPPED the frame whenever the socket
    // was null (post-disconnect, pre-reconnect): optional chaining short-
    // circuits, so nothing throws and the catch that re-queues never runs.
    // A null socket is exactly when a frame most needs to be kept.
    if (!this.socket) { this.queue(frame); return }
    try { this.socket.send(encodeFrame(frame)) } catch { this.queue(frame) }
  }

  private onClosed(): void {
    this.authed = false
    this.socket = null
    if (this.stopped) { this.state = 'closed'; return }
    this.state = 'idle'
    // Exponential backoff with jitter; reconnect re-auths, re-opens, re-syncs.
    const base = this.opts.backoffBaseMs ?? 500
    const max  = this.opts.backoffMaxMs ?? 15_000
    const delay = Math.min(max, base * 2 ** this.attempt) * (0.5 + Math.random() * 0.5)
    this.attempt++
    // If we've never once connected after several tries, the relay is likely
    // unreachable (wrong URL, server down, blocked) — say so, but keep retrying.
    if (this.attempt >= 3 && !this.everAuthed) {
      this.opts.onStatusChange?.('reconnecting', "Can't reach chat — retrying…")
    }
    this.timer = setTimeout(() => this.connect(), delay)
  }
}

/** Connection-fatal error codes — retrying can't fix these. ONLY auth-handshake
 *  failure qualifies: FORBIDDEN / NOT_FOUND are per-REQUEST errors (a stale
 *  reference, one permission check) and must NOT tear down the whole socket. */
const FATAL_ERRORS = new Set(['UNAUTHORIZED'])
function friendlyError(code: string): string {
  switch (code) {
    case 'UNAUTHORIZED': return 'Chat unavailable — sign-in/token was rejected'
    default:             return 'Chat unavailable'
  }
}

function defaultFactory(url: string): SocketLike {
  return new WebSocket(url) as unknown as SocketLike
}
